import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Typography,
  Chip,
  Alert,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Veiculo, Departamento, Subdepartamento } from '../types';
import { veiculoService } from '../services/veiculoService';
import { departamentoService } from '../services/departamentoService';
import { subdepartamentoService } from '../services/subdepartamentoService';
import { getErrorMessage } from '../utils/errors';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

const schema = yup.object({
  placa: yup.string().required('Placa é obrigatória').min(7, 'Placa deve ter pelo menos 7 caracteres'),
  modelo: yup.string().required('Modelo é obrigatório'),
  marca: yup.string().required('Marca é obrigatória'),
  ano: yup.number().required('Ano é obrigatório').min(1900).max(2100),
  quilometragemAtual: yup.number().required('Quilometragem é obrigatória').min(0),
  capacidadeTanque: yup.number().required('Capacidade do tanque é obrigatória').min(0),
  tipoCombustivel: yup.string().required('Tipo de combustível é obrigatório'),
  tipoFrota: yup.string().required('Tipo de frota é obrigatório'),
  departamentoId: yup
    .number()
    .transform((v) => (v === '' || v === null || isNaN(v) ? null : v))
    .required('Departamento é obrigatório'),
  subdepartamentoId: yup
    .number()
    .transform((v) => (v === '' || v === null || isNaN(v) ? null : v))
    .nullable(),
}).required();

const Veiculos: React.FC = () => {
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [open, setOpen] = useState(false);
  const [editingVeiculo, setEditingVeiculo] = useState<Veiculo | null>(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null; departamentos: string[]; motoristas: string[] }>({ open: false, id: null, departamentos: [], motoristas: [] });
  const [sucesso, setSucesso] = useState('');
  const [mensagemErro, setMensagemErro] = useState('');
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [subdepartamentos, setSubdepartamentos] = useState<Subdepartamento[]>([]);
  const [filtro, setFiltro] = useState('');
  const { hasRole } = useAuth();
  const podeExcluir = hasRole('ADMIN') || hasRole('GERENTE');

  const { register, control, handleSubmit, reset, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });
  const departamentoSelecionado = watch('departamentoId');

  useEffect(() => {
    loadVeiculos();
    loadDepartamentos();
  }, []);

  const loadDepartamentos = async () => {
    try {
      const [deps, subs] = await Promise.all([
        departamentoService.listarAcessiveis(),
        subdepartamentoService.listar(),
      ]);
      setDepartamentos(deps);
      setSubdepartamentos(subs);
    } catch (error) {
      console.error('Erro ao carregar departamentos', error);
    }
  };

  const loadVeiculos = async () => {
    try {
      const data = await veiculoService.listar();
      setVeiculos(data);
    } catch (error) {
      console.error('Erro ao carregar veículos', error);
    }
  };

  const veiculoVazio = () => ({
    tipoCombustivel: 'FLEX',
    tipoFrota: 'PROPRIO',
    departamentoId: departamentos[0]?.id ?? '',
    subdepartamentoId: '',
  });

  const handleOpen = (veiculo?: Veiculo) => {
    if (veiculo) {
      setEditingVeiculo(veiculo);
      reset(veiculo);
    } else {
      setEditingVeiculo(null);
      reset(veiculoVazio() as any);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingVeiculo(null);
    reset(veiculoVazio() as any);
    setErro('');
  };

  const onSubmit = async (data: any) => {
    setSalvando(true);
    try {
      if (editingVeiculo?.id) {
        const payload = { ...data, status: editingVeiculo?.status }; await veiculoService.atualizar(editingVeiculo.id, payload);
      } else {
        await veiculoService.cadastrar(data);
      }
      loadVeiculos();
      handleClose();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = (id: number) => {
    const veiculo = veiculos.find((v) => v.id === id);
    const departamentos = [veiculo?.departamentoNome, veiculo?.subdepartamentoNome].filter(Boolean) as string[];
    const motoristas = veiculo?.motoristasVinculados || [];
    setConfirmDelete({ open: true, id, departamentos, motoristas });
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete.id) return;
    try {
      const resp = await veiculoService.deletar(confirmDelete.id);
      loadVeiculos();
      setSucesso(resp?.mensagem || 'Operação realizada com sucesso');
      setMensagemErro('');
      setConfirmDelete({ open: false, id: null, departamentos: [], motoristas: [] });
    } catch (error) {
      console.error('Erro ao deletar veículo', error);
      setMensagemErro(getErrorMessage(error));
      setConfirmDelete({ open: false, id: null, departamentos: [], motoristas: [] });
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'ATIVO': return 'success';
      case 'EM_MANUTENCAO': return 'warning';
      case 'INATIVO': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'ATIVO': return 'Ativo';
      case 'EM_MANUTENCAO': return 'Em Manutenção';
      case 'INATIVO': return 'Inativo';
      default: return status;
    }
  };

  const getTipoFrotaLabel = (tipo?: string) => {
    switch (tipo) {
      case 'PROPRIO': return 'Próprio';
      case 'CEDIDO': return 'Cedido';
      case 'LOCADO': return 'Locado';
      default: return tipo;
    }
  };

  const termo = filtro.trim().toLowerCase();
  const veiculosFiltrados = termo
    ? veiculos.filter((v) =>
        [v.placa, v.modelo, v.marca, v.departamentoNome, v.tipoCombustivel, v.tipoFrota, v.status]
          .filter(Boolean)
          .some((campo) => String(campo).toLowerCase().includes(termo))
      )
    : veiculos;

  return (
    <Container disableGutters maxWidth={false}>
      {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}
      {mensagemErro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMensagemErro('')}>{mensagemErro}</Alert>}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Veículos</Typography>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            Novo Veículo
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Placa</TableCell>
              <TableCell>Modelo</TableCell>
              <TableCell>Marca</TableCell>
              <TableCell>Ano</TableCell>
              <TableCell>Km Atual</TableCell>
              <TableCell>Combustível</TableCell>
              <TableCell>Departamento</TableCell>
              <TableCell>Tipo de Frota</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {veiculosFiltrados.map((veiculo) => (
              <TableRow key={veiculo.id}>
                <TableCell><strong>{veiculo.placa}</strong></TableCell>
                <TableCell>{veiculo.modelo}</TableCell>
                <TableCell>{veiculo.marca}</TableCell>
                <TableCell>{veiculo.ano}</TableCell>
                <TableCell>{veiculo.quilometragemAtual?.toLocaleString('pt-BR')} km</TableCell>
                <TableCell>{veiculo.tipoCombustivel}</TableCell>
                <TableCell>{veiculo.departamentoNome ?? '-'}</TableCell>
                <TableCell>{getTipoFrotaLabel(veiculo.tipoFrota)}</TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(veiculo.status)}
                    color={getStatusColor(veiculo.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(veiculo)}>
                    <Edit />
                  </IconButton>
                  {podeExcluir && (
                    <IconButton size="small" color="error" onClick={() => veiculo.id && handleDelete(veiculo.id)}>
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingVeiculo ? 'Editar Veículo' : 'Novo Veículo'}</DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          <form id="veiculo-form" onSubmit={handleSubmit(onSubmit as any)}>
            <Box display="flex" gap={2} alignItems="flex-start">
              <TextField fullWidth label="Placa" {...register('placa')} error={!!errors.placa} helperText={errors.placa?.message} margin="normal" />
              <TextField fullWidth label="Modelo" {...register('modelo')} error={!!errors.modelo} helperText={errors.modelo?.message} margin="normal" />
              <TextField fullWidth label="Marca" {...register('marca')} error={!!errors.marca} helperText={errors.marca?.message} margin="normal" />
            </Box>
            <Box display="flex" gap={2} alignItems="flex-start">
              <TextField fullWidth label="Ano" type="number" {...register('ano')} error={!!errors.ano} helperText={errors.ano?.message} margin="normal" />
              <TextField fullWidth label="Quilometragem Atual" type="number" {...register('quilometragemAtual')} error={!!errors.quilometragemAtual} helperText={errors.quilometragemAtual?.message} margin="normal" />
              <TextField fullWidth label="Capacidade do Tanque (L)" type="number" {...register('capacidadeTanque')} error={!!errors.capacidadeTanque} helperText={errors.capacidadeTanque?.message} margin="normal" />
              <Controller
                name="tipoCombustivel"
                control={control}
                render={({ field }) => (
                  <TextField select fullWidth label="Tipo de Combustível" name={field.name} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} error={!!errors.tipoCombustivel} helperText={errors.tipoCombustivel?.message} margin="normal">
                    <MenuItem value="GASOLINA">Gasolina</MenuItem>
                    <MenuItem value="DIESEL">Diesel</MenuItem>
                    <MenuItem value="ETANOL">Etanol</MenuItem>
                    <MenuItem value="FLEX">Flex</MenuItem>
                  </TextField>
                )}
              />
              <Controller
                name="tipoFrota"
                control={control}
                render={({ field }) => (
                  <TextField select fullWidth label="Tipo de Frota" name={field.name} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} error={!!errors.tipoFrota} helperText={errors.tipoFrota?.message} margin="normal">
                    <MenuItem value="PROPRIO">Próprio</MenuItem>
                    <MenuItem value="CEDIDO">Cedido</MenuItem>
                    <MenuItem value="LOCADO">Locado</MenuItem>
                  </TextField>
                )}
              />
            </Box>
            <Controller
              name="departamentoId"
              control={control}
              render={({ field }) => (
                <TextField select fullWidth label="Departamento" name={field.name} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} error={!!errors.departamentoId} helperText={errors.departamentoId?.message} margin="normal">
                  {departamentos.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.nome}</MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="subdepartamentoId"
              control={control}
              render={({ field }) => (
                <TextField select fullWidth label="Subdepartamento" name={field.name} value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} margin="normal">
                  <MenuItem value="">Não informado</MenuItem>
                  {subdepartamentos
                    .filter((sub) => !departamentoSelecionado || sub.departamentoId === Number(departamentoSelecionado))
                    .map((sub) => (
                      <MenuItem key={sub.id} value={sub.id}>{sub.nome} {sub.departamentoNome ? `(${sub.departamentoNome})` : ''}</MenuItem>
                    ))}
                </TextField>
              )}
            />
          </form>
        </DialogContent>
        <DialogActions>
          {editingVeiculo && (
            <FormControlLabel
              sx={{ mr: 'auto' }}
              control={
                <Switch
                  checked={editingVeiculo.status === 'ATIVO'}
                  disabled={editingVeiculo.status === 'ATIVO'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setEditingVeiculo((prev) => (prev ? { ...prev, status: 'ATIVO' as any } : prev));
                    }
                  }}
                />
              }
              label={editingVeiculo.status === 'ATIVO' ? 'Ativo' : 'Inativo (clique pra reativar)'}
            />
          )}
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="submit" form="veiculo-form" variant="contained" disabled={salvando}>
            {salvando ? 'Salvando...' : (editingVeiculo ? 'Atualizar' : 'Cadastrar')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete.open}
        title="Confirmar exclusão"
        message={
          confirmDelete.departamentos.length || confirmDelete.motoristas.length
            ? `Este veículo está vinculado a ${[
                confirmDelete.departamentos.length ? `departamento(s): ${confirmDelete.departamentos.join(', ')}` : '',
                confirmDelete.motoristas.length ? `motorista(s): ${confirmDelete.motoristas.join(', ')}` : '',
              ].filter(Boolean).join(' e ')}. Deseja realmente excluir?`
            : 'Tem certeza que deseja excluir este veículo?'
        }
        confirmText="Excluir"
        severity="error"
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete({ open: false, id: null, departamentos: [], motoristas: [] })}
      />
    </Container>
  );
};

export default Veiculos;
